import {
    useEffect,
    useState
} from "react";

import {
    IContextBase,
    IEndpointOutputNameElement
} from "../lib/types";
import { DropdownMenu } from "./DropdownMenu";
import { MultiselectInputField } from "./MultiselectInputField";
import { IEndpointSplitByFieldEx } from "../screens/NewEndpoint";
import { TextboxModal } from "./TextboxModal";

type EndpointOutputNameProps = {
    contexts: IContextBase[];
    split_fields_data: IEndpointSplitByFieldEx[];
    elements: IEndpointOutputNameElement[];
    placeholder: string;
    addElement: (element: IEndpointOutputNameElement) => void;
    removeElement: (element_idx: number) => void;
    title: string;
    separator?: "underline" | "space";
    single_line?: boolean;
    constant_suggestions?: string[]; // optional dropdown suggestions for constants
}

interface IOutputNameField {
    context_uuid: string;
    field_uuid: string;
    field_name: string;
}

export function EndpointOutputName(props: EndpointOutputNameProps) {
    const { contexts, split_fields_data, elements, addElement, removeElement, title, separator, placeholder, single_line, constant_suggestions } = props;

    const [fields, setFields] = useState<IOutputNameField[]>([]);
    const [showCustomTextInput, setShowCustomTextInput] = useState(false);

    useEffect(() => {
        const already_added_fields = new Set<string>();
        // initialize fields with object contexts
        const new_fields: IOutputNameField[] = contexts
            .filter(c => c.type === "object")
            .flatMap(c => c.fields.map(f => ({
                context_uuid: c.uuid,
                field_uuid: f.uuid,
                field_name: f.name
            })));
        for (const field of new_fields) {
            already_added_fields.add(field.field_uuid);
        }
        // we can also add fields from template_split_fields
        for (const split_field of split_fields_data) {
            const { context_uuid, field_name, field_uuid } = split_field;
            // check if field is already in new_fields
            if (already_added_fields.has(field_uuid)) {
                continue;
            }
            new_fields.push({
                context_uuid,
                field_uuid,
                field_name
            });
            already_added_fields.add(field_uuid);
        }
        // if split_field context_type is object, also add it
        for (const split_field of split_fields_data) {
            const { context, context_type } = split_field;
            if (context_type === "object") {
                for (const field of context.fields) {
                    if (already_added_fields.has(field.uuid)) {
                        continue;
                    }
                    new_fields.push({
                        context_uuid: context.uuid,
                        field_uuid: field.uuid,
                        field_name: field.name
                    });
                    already_added_fields.add(field.uuid);
                }
            }
        }
        // add also rest of the fields but with disclaimer
        for (const context of contexts) {
            if (context.type !== "object") {
                for (const field of context.fields) {
                    if (already_added_fields.has(field.uuid)) {
                        continue;
                    }
                    new_fields.push({
                        context_uuid: context.uuid,
                        field_uuid: field.uuid,
                        field_name: `${field.name} [multiple values possible]`
                    });
                    already_added_fields.add(field.uuid);
                }
            }
        }
        setFields(new_fields);
    }, [contexts, split_fields_data]);

    const handleAddConstant = (result: boolean, constant_text?: string) => {
        if (result) {
            const cleaned_constant_text = constant_text?.trim() ?? "";
            if (cleaned_constant_text !== "") {
                addElement({ type: "constant", constant: cleaned_constant_text });
            }
        }
        setShowCustomTextInput(false);
    };

    const getDisplayValue = (element: IEndpointOutputNameElement, index: number): string => {
        if (element.type === "field") {
            const field = fields.find(f => f.context_uuid === element.context_uuid && f.field_uuid === element.field_uuid);
            return field?.field_name || "";
        }
        return `"${element.constant}"`;
    };

    const dropdownItems = [
        {
            title: "Custom text...",
            onClick: () => setShowCustomTextInput(true),
            separator: true
        },
        ...(constant_suggestions && constant_suggestions.length > 0 ?
            constant_suggestions.map((suggestion, index) => ({
                title: suggestion,
                onClick: () => { addElement({ type: "constant", constant: suggestion }) },
                separator: index === constant_suggestions.length - 1
            })) : []),
        ...fields.map((f) => ({
            title: f.field_name,
            onClick: () => { addElement({ type: "field", context_uuid: f.context_uuid, field_uuid: f.field_uuid }) }
        }))
    ];

    return single_line ?
        <div className="w-full grid grid-cols-5 items-center">
            <label className="flex-grow text-sm font-medium text-gray-700">{title}</label>
            <div className="flex flex-row items-center gap-2 col-span-4">
                <div className="flex-grow">
                    <MultiselectInputField
                        values={elements.map(getDisplayValue)}
                        placeholder={placeholder}
                        separator={separator || "underline"}
                        onRemove={removeElement}
                    />
                </div>
                <DropdownMenu title="Add field" align="right" items={dropdownItems} />
            </div>
            <TextboxModal
                open={showCustomTextInput}
                title="Custom text"
                onClose={handleAddConstant}
            />
        </div> :
        <div className="w-full flex flex-col">
            <div className="flex flex-row items-center">
                <label className="flex-grow text-sm font-medium text-gray-700">{title}</label>
                <DropdownMenu title="Add field" align="right" items={dropdownItems}
                />
            </div>
            <TextboxModal
                open={showCustomTextInput}
                title="Custom text"
                onClose={handleAddConstant}
            />
            <div className="mt-3 w-full">
                <MultiselectInputField
                    values={elements.map(getDisplayValue)}
                    placeholder={placeholder}
                    separator={separator || "underline"}
                    onRemove={removeElement}
                />
            </div>
        </div>;
}
